import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  GitCompareArrows, Play, Download, ArrowRight, Upload, Loader2, AlertTriangle,
  Info, FileText,
} from 'lucide-react';
import { API } from '@/App';

export default function Compare() {
  const navigate = useNavigate();
  const [sources, setSources] = useState({ integrated: [], batches: [] });
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [test, setTest] = useState('t_test');
  const [matchBy, setMatchBy] = useState('sample_id');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState({});
  const [csvA, setCsvA] = useState(null);
  const [csvB, setCsvB] = useState(null);
  const cfg = { withCredentials: true };

  const setB_ = (k, v) => setBusy(p => ({ ...p, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/phenoscan/compare/available-sources`, cfg)
      .then(r => setSources(r.data))
      .catch(() => {});
  }, []);

  // Parse a CSV File → array of row-dicts
  const parseCsv = (file) => new Promise(res => {
    const reader = new FileReader();
    reader.onload = () => {
      const txt = String(reader.result || '');
      const lines = txt.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) return res([]);
      const header = lines[0].split(',').map(h => h.trim());
      const out = lines.slice(1).map(line => {
        const parts = line.split(',');
        const row = {};
        header.forEach((h, i) => {
          const raw = (parts[i] ?? '').trim();
          const num = Number(raw);
          row[h] = raw === '' ? null : (Number.isFinite(num) && raw !== '' ? num : raw);
        });
        return row;
      });
      res(out);
    };
    reader.readAsText(file);
  });

  const chooseSource = (side, opt) => {
    if (side === 'a') setA(opt);
    else setB(opt);
  };

  const handleCsvUpload = async (side, file) => {
    if (!file) return;
    const rows = await parseCsv(file);
    if (!rows.length) { toast.error(`Empty CSV for group ${side.toUpperCase()}`); return; }
    const spec = { kind: 'inline', rows, label: file.name.replace(/\.csv$/i, '') };
    if (side === 'a') { setA(spec); setCsvA(file); }
    else { setB(spec); setCsvB(file); }
    toast.success(`Loaded ${rows.length} rows for ${side.toUpperCase()}`);
  };

  const runCompare = useCallback(async () => {
    if (!a || !b) { toast.error('Pick Group A and Group B'); return; }
    setB_('run', true); setResult(null);
    try {
      const r = await axios.post(`${API}/phenoscan/compare/run`, {
        a, b, test, match_by: matchBy, bh_alpha: 0.05,
      }, cfg);
      setResult(r.data);
    } catch (e) {
      toast.error(`Compare failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setB_('run', false); }
  }, [a, b, test, matchBy]);

  const exportFile = async (fmt) => {
    try {
      const r = await axios.get(`${API}/phenoscan/compare/export?fmt=${fmt}`,
        { ...cfg, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const ae = document.createElement('a');
      ae.href = url; ae.download = `phenoscan_compare.${fmt}`; ae.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Export failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const sendToIntegrated = async () => {
    setB_('handoff', true);
    try {
      const r = await axios.post(`${API}/phenoscan/compare/handoff-integrated`, {}, cfg);
      toast.success(`${r.data.rows_ingested} rows (A+B + group col) → Integrated · compare`);
      navigate('/phenoscan?tab=integrated');
    } catch (e) {
      toast.error(`Hand-off failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setB_('handoff', false); }
  };

  const sourceOptions = useMemo(() => {
    const o = [];
    sources.integrated.forEach(s => o.push(s));
    sources.batches.forEach(s => o.push(s));
    return o;
  }, [sources]);

  const maxAbsDiff = useMemo(() => {
    if (!result) return 1;
    return Math.max(1e-9, ...result.per_trait.map(t =>
      t.mean_diff === null ? 0 : Math.abs(t.mean_diff)));
  }, [result]);

  return (
    <div data-testid="phenoscan-compare-pane" className="space-y-4">
      <Section icon={GitCompareArrows} title="1. Pick two groups">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GroupPicker side="a" current={a} onChoose={opt => chooseSource('a', opt)}
            options={sourceOptions} onCsv={f => handleCsvUpload('a', f)} csvName={csvA?.name} />
          <GroupPicker side="b" current={b} onChoose={opt => chooseSource('b', opt)}
            options={sourceOptions} onCsv={f => handleCsvUpload('b', f)} csvName={csvB?.name} />
        </div>
      </Section>

      <Section icon={Info} title="2. Statistics">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex gap-3 text-xs" data-testid="compare-test-radio">
            <span className="font-semibold" style={{ color: '#1B4332' }}>Test:</span>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="radio" checked={test === 't_test'}
                onChange={() => setTest('t_test')} /> Welch's t-test
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="radio" checked={test === 'mann_whitney'}
                onChange={() => setTest('mann_whitney')} /> Mann-Whitney U
            </label>
          </div>
          <div className="flex gap-3 text-xs" data-testid="compare-match-radio">
            <span className="font-semibold" style={{ color: '#1B4332' }}>Match:</span>
            {[['sample_id','by sample_id'],['genotype','by genotype'],['none','independent groups']].map(([k,l]) => (
              <label key={k} className="flex items-center gap-1 cursor-pointer">
                <input type="radio" checked={matchBy === k} onChange={() => setMatchBy(k)} /> {l}
              </label>
            ))}
          </div>
          <button onClick={runCompare} disabled={busy.run || !a || !b}
            data-testid="compare-run-btn"
            className="ml-auto px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
            style={{ background: '#2D6A4F', color: 'white', opacity: (!a || !b) ? 0.5 : 1 }}>
            {busy.run ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            Run comparison
          </button>
        </div>
        <div className="text-[11px] mt-2" style={{ color: '#6F695F' }}>
          Benjamini-Hochberg FDR correction (α = 0.05) applied across all traits.
          Welch's t-test assumes independent samples with possibly unequal variances.
          Mann-Whitney U is the non-parametric alternative for non-normal distributions or very small N.
        </div>
      </Section>

      {result && <ResultBlock result={result} maxAbsDiff={maxAbsDiff}
        onExportCsv={() => exportFile('csv')}
        onExportXlsx={() => exportFile('xlsx')}
        onHandoff={sendToIntegrated}
        handoffBusy={busy.handoff} />}
    </div>
  );
}


function GroupPicker({ side, current, onChoose, options, onCsv, csvName }) {
  const testId = `compare-group-${side}`;
  const label = side.toUpperCase();
  return (
    <div className="rounded-lg p-3" style={{ border: '1px solid #E5E5E0', background: 'white' }}
      data-testid={testId}>
      <div className="font-semibold text-sm mb-2" style={{ color: '#1B4332' }}>
        Group {label}
        {current && <span className="ml-2 text-[11px] font-normal" style={{ color: '#2D6A4F' }}>
          · {current.label}{current.kind !== 'inline' ? ` (${current.n_rows} rows)` : ''}</span>}
      </div>
      <div className="flex flex-wrap gap-1 mb-2">
        {options.map((opt, i) => (
          <button key={`${opt.kind}-${opt.ref}-${i}`}
            onClick={() => onChoose(opt)}
            data-testid={`compare-pick-${side}-${opt.kind}-${opt.ref}`}
            className="text-[11px] px-2 py-0.5 rounded"
            style={{
              border: current && current.kind === opt.kind && current.ref === opt.ref
                ? '1px solid #2D6A4F' : '1px solid #E5E5E0',
              background: current && current.kind === opt.kind && current.ref === opt.ref
                ? '#D1FAE5' : 'white',
              color: '#1B4332',
            }}>
            {opt.label}
          </button>
        ))}
      </div>
      <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer px-2 py-1 rounded"
        style={{ border: '1px solid #E5E5E0' }}
        data-testid={`compare-upload-csv-${side}`}>
        <Upload size={10} /> {csvName ? `CSV: ${csvName}` : 'Upload CSV'}
        <input type="file" accept=".csv" className="hidden"
          onChange={e => onCsv(e.target.files?.[0])} />
      </label>
    </div>
  );
}


function ResultBlock({ result, maxAbsDiff, onExportCsv, onExportXlsx, onHandoff, handoffBusy }) {
  const summary = result.summary || {};
  return (
    <Section icon={FileText} title="3. Results"
      right={
        <div className="flex gap-2">
          <button onClick={onExportCsv} data-testid="compare-export-csv"
            className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
            style={{ background: 'white', border: '1px solid #2D6A4F', color: '#2D6A4F' }}>
            <Download size={12} /> CSV
          </button>
          <button onClick={onExportXlsx} data-testid="compare-export-xlsx"
            className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
            style={{ background: 'white', border: '1px solid #2D6A4F', color: '#2D6A4F' }}>
            <Download size={12} /> XLSX
          </button>
          <button onClick={onHandoff} disabled={handoffBusy}
            data-testid="compare-handoff-integrated"
            className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
            style={{ background: '#1B4332', color: 'white' }}>
            {handoffBusy ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
            Send to Integrated
          </button>
        </div>
      }>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        <Stat label="Group A" value={`${result.label_a} (n=${result.n_a})`} />
        <Stat label="Group B" value={`${result.label_b} (n=${result.n_b})`} />
        <Stat label="Traits" value={result.n_traits} />
        <Stat label="Sig (BH)" value={`${summary.n_significant_bh} / ${summary.total_traits}`} accent />
        <Stat label="Sig %" value={`${(summary.pct_significant_bh || 0).toFixed(0)}%`} />
      </div>

      {summary.small_n_warning && (
        <div className="rounded-lg p-2 mb-3 text-xs flex items-center gap-1.5"
          style={{ background: '#FEF3C7', color: '#78350F', border: '1px solid #FDE68A' }}
          data-testid="compare-small-n-warning">
          <AlertTriangle size={12} />
          Small N detected (n &lt; 3 in at least one group). P-values and FDR q-values are unreliable at this sample size.
        </div>
      )}

      {result.join_method !== 'none' && (
        <div className="text-[11px] mb-2" style={{ color: '#6F695F' }}>
          Join by <code>{result.join_method}</code>:
          shared = <b>{result.ids_shared_n}</b>,
          only in A = {result.ids_only_a_n},
          only in B = {result.ids_only_b_n}
        </div>
      )}

      {/* Per-trait table */}
      <div className="overflow-auto rounded border" style={{ borderColor: '#E5E5E0', maxHeight: 320 }}>
        <table className="text-[11px]" data-testid="compare-per-trait-table">
          <thead><tr style={{ background: '#F3F6F3', position: 'sticky', top: 0 }}>
            {['Trait', `${result.label_a}\nmean ± SD`, `${result.label_b}\nmean ± SD`,
              'Δ (B − A)', '95% CI', 'p', 'q (BH)', "Cohen's d", 'Sig', 'Test'
            ].map(h => <th key={h} className="px-2 py-1 text-left whitespace-pre">{h}</th>)}
          </tr></thead>
          <tbody>
            {result.per_trait.map(t => (
              <tr key={t.trait} style={{ borderTop: '1px solid #F3F6F3' }}>
                <td className="px-2 py-0.5 font-mono">{t.trait}</td>
                <td className="px-2 py-0.5">{fmt(t.mean_a)} ± {fmt(t.sd_a)}</td>
                <td className="px-2 py-0.5">{fmt(t.mean_b)} ± {fmt(t.sd_b)}</td>
                <td className="px-2 py-0.5">{fmt(t.mean_diff)}</td>
                <td className="px-2 py-0.5">{t.ci95_lo === null ? '—' :
                  `[${fmt(t.ci95_lo)}, ${fmt(t.ci95_hi)}]`}</td>
                <td className="px-2 py-0.5 font-mono">{fmt(t.p_value, 3)}</td>
                <td className="px-2 py-0.5 font-mono">{fmt(t.q_value_bh, 3)}</td>
                <td className="px-2 py-0.5">{fmt(t.cohen_d)}</td>
                <td className="px-2 py-0.5 font-semibold"
                  style={{ color: t.significant_bh ? '#065F46' : '#6F695F' }}>
                  {t.star || ''}
                </td>
                <td className="px-2 py-0.5" style={{ color: '#6F695F' }}>{t.test}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Diff bar chart */}
      <div className="mt-3" data-testid="compare-bar-chart">
        <div className="text-xs font-semibold mb-1" style={{ color: '#1B4332' }}>
          Mean difference (B − A) per trait
        </div>
        <div className="space-y-1">
          {result.per_trait.map(t => {
            const d = t.mean_diff ?? 0;
            const pct = Math.abs(d) / maxAbsDiff;
            const isPos = d >= 0;
            return (
              <div key={t.trait} className="flex items-center gap-2 text-[11px]">
                <div className="w-28 font-mono truncate" title={t.trait}>{t.trait}</div>
                <div className="flex-1 h-4 relative" style={{ background: '#F3F6F3' }}>
                  <div style={{
                    position: 'absolute', top: 0, bottom: 0,
                    [isPos ? 'left' : 'right']: '50%',
                    width: `${pct * 50}%`,
                    background: isPos ? '#2D6A4F' : '#C026D3',
                  }} />
                  <div style={{
                    position: 'absolute', top: 0, bottom: 0, left: '50%', width: '1px',
                    background: '#6F695F',
                  }} />
                </div>
                <div className="w-24 text-right font-mono">
                  {fmt(d)} {t.star && <b style={{ color: '#065F46' }}>{t.star}</b>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}


function Section({ icon: Icon, title, right, children }) {
  return (
    <div className="rounded-xl bg-white p-4" style={{ border: '1px solid #E5E5E0' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color: '#2D6A4F' }} />
          <div className="font-display text-base" style={{ color: '#1B4332' }}>{title}</div>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-md p-2" style={{ background: accent ? '#D1FAE5' : '#F3F6F3' }}>
      <div className="text-[10px] uppercase" style={{ color: '#6F695F', letterSpacing: 0.5 }}>{label}</div>
      <div className="font-semibold text-sm" style={{ color: accent ? '#065F46' : '#1B4332' }}>
        {value}
      </div>
    </div>
  );
}

function fmt(v, n = 3) {
  if (v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v))) return '—';
  if (typeof v === 'number') {
    if (Math.abs(v) < 1e-4 && v !== 0) return v.toExponential(2);
    return v.toFixed(n);
  }
  return String(v);
}
